import { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';

import type { ReportView } from './api/reportViewTypes';

/**
 * Shown when opening a shared / link view that the current user does not own.
 * Creates a private fork so they can edit without changing the original.
 */
type CloneReportViewModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    view: ReportView | null;
    isCloning?: boolean;
    onClone: (name: string) => void;
};

export function CloneReportViewModal({
    open,
    onOpenChange,
    view,
    isCloning = false,
    onClone,
}: CloneReportViewModalProps) {
    const [name, setName] = useState('');

    useEffect(() => {
        if (open && view) {
            setName(`${view.name} (copy)`);
        }
    }, [open, view]);

    if (!view) {
        return null;
    }

    const trimmedName = name.trim();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="reports-light-overlay w-[calc(100%-2rem)] overflow-hidden sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="h-4 w-4" />
                        Clone this view
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div className="space-y-2 text-sm text-muted-foreground">
                            <p>
                                You’re viewing a shared report view. Cloning creates your own copy
                                with the same columns, filters, and sort.
                            </p>
                            <p>
                                After cloning, you can change anything you want without affecting
                                the original. Your copy will appear under My views.
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>

                <div className="min-w-0 space-y-2 py-2">
                    <Label htmlFor="clone-report-view-name">Name for your copy</Label>
                    <Input
                        id="clone-report-view-name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="e.g. Sales by Day (my copy)"
                        maxLength={120}
                        autoFocus
                        className="w-full min-w-0"
                    />
                </div>

                <DialogFooter className="min-w-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCloning}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={!trimmedName || isCloning}
                        onClick={() => onClone(trimmedName)}
                    >
                        {isCloning ? 'Cloning...' : 'Clone view'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
